import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

// Legacy localStorage key (pre-httpOnly-cookie). On mount we wipe it so we
// don't keep a stale bearer token around indefinitely. Cookie is now the
// source of truth.
const LEGACY_TOKEN_KEY = "uptick_token";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, just call /auth/me — the browser sends the session cookie
  // automatically (or omits it if logged out, in which case we get 401).
  useEffect(() => {
    let cancelled = false;
    try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch (_) { /* noop */ }
    api.get("/auth/me")
      .then((res) => { if (!cancelled) setUser(res.data); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const register = useCallback(async (email, password, name, extra = {}) => {
    const res = await api.post("/auth/register", { email, password, name, ...extra });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    // Optimistically clear local user state first so the UI reacts immediately
    // (no flash of authed content during the network round-trip). The cookie
    // clear happens server-side regardless of whether the request succeeds.
    setUser(null);
    try {
      await api.post("/auth/logout");
    } catch (_) {
      /* swallow — local state is the source of truth */
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
